import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search } from 'lucide-react';

const ALL = '__all__';
const GROUPS = ['All', 'Hot', 'Most Active', 'Cold'];
const cls = 'bg-white border-gray-300 h-9 text-sm';

export function AudiencePicker({ selected, setSelected }) {
  const [filters, setFilters] = useState({ group: '', tag: '', location: '' });
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ buyers: [], tags: [], locations: [], group_counts: {} });

  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    API.get('/buyer-intel/buyers', { params }).then(r => {
      setData(r.data);
      setSelected(new Set(r.data.buyers.filter(b => b.email).map(b => b.id)));
    }).catch(() => {});
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setFilters(p => ({ ...p, [k]: v === ALL || v === 'All' ? '' : v }));
  const toggle = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const shown = data.buyers.filter(b => !search || `${b.name} ${b.email} ${b.company}`.toLowerCase().includes(search.toLowerCase()));
  const withEmail = data.buyers.filter(b => b.email);

  return (
    <div className="space-y-2" data-testid="audience-picker">
      <div className="flex flex-wrap gap-1.5">
        {GROUPS.map(g => (
          <button key={g} data-testid={`audience-group-${g.toLowerCase().replace(/\s+/g, '-')}`} onClick={() => set('group', g)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${(filters.group || 'All') === g ? 'bg-[#0073ea] text-white border-[#0073ea]' : 'bg-white text-gray-600 border-gray-200'}`}>
            {g} {data.group_counts[g] !== undefined && filters.group === '' ? `(${data.group_counts[g] || 0})` : ''}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Select value={filters.tag || ALL} onValueChange={v => set('tag', v)}>
          <SelectTrigger data-testid="audience-tag" className={cls}><SelectValue placeholder="Any tag" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value={ALL}>Any tag</SelectItem>{data.tags.map(([t]) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.location || ALL} onValueChange={v => set('location', v)}>
          <SelectTrigger data-testid="audience-location" className={cls}><SelectValue placeholder="Any location" /></SelectTrigger>
          <SelectContent className="bg-white max-h-64"><SelectItem value={ALL}>Any location</SelectItem>{data.locations.map(([l]) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input data-testid="audience-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Find buyer" className={`${cls} pl-8`} />
        </div>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span data-testid="audience-count" className="font-semibold text-gray-900">{selected.size} recipient{selected.size !== 1 ? 's' : ''} selected</span>
        <span className="flex gap-3">
          <button data-testid="audience-select-all" onClick={() => setSelected(new Set(withEmail.map(b => b.id)))} className="text-[#0073ea] hover:underline">Select all</button>
          <button data-testid="audience-select-none" onClick={() => setSelected(new Set())} className="text-gray-500 hover:underline">None</button>
        </span>
      </div>
      <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
        {shown.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No buyers match</p>}
        {shown.map(b => (
          <label key={b.id} className={`flex items-center gap-2 px-3 py-1.5 text-sm ${b.email ? 'cursor-pointer hover:bg-gray-50' : 'opacity-50'}`}>
            <Checkbox data-testid={`audience-buyer-${b.id}`} checked={selected.has(b.id)} disabled={!b.email} onCheckedChange={() => toggle(b.id)} />
            <span className="flex-1 truncate text-gray-900">{b.name}</span>
            <span className="text-[11px] text-gray-400 truncate">{b.email || 'no email'}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
